import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Document } from '@/features/docs/Document'
import { type DocumentAccess } from '@/features/docs/DocumentAccess'
import { type DocumentStore } from '@/features/docs/DocumentStore'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { type JobGist } from '@/features/jobs/JobGist'
import type { FetchJobGistFn } from '@/features/jobs'
import { anyAbortSignal } from '@/utils/abort-util'
import type { PromptDeleteDocumentFn } from '@/features/docs'

/**
 * Details about the access context, providing additional information
 * about what the document is linked to.
 */
export type AccessDetails = JobAccessDetails | UnknownAccessDetails

/**
 * Details about the access context, providing additional information
 * about what the document is linked to.
 */
export interface JobAccessDetails {
  type: 'Job'
  job: JobGist
}

/**
 * Unknown access details.
 */
export interface UnknownAccessDetails {
  type: 'Unknown'
}

/**
 * View model for the unlink document dialog.
 */
export class UnlinkDocumentDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<false | 'unlinked' | 'deleted'>({
    abort$: this.modal.close$,
  })

  @observable accessor document: Document | null = null
  @observable accessor access: DocumentAccess | null = null
  @observable accessor accessDetails: AccessDetails = { type: 'Unknown' }

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
    private readonly _fetchJobGist: FetchJobGistFn,
    private readonly _promptDelete: PromptDeleteDocumentFn,
  ) {}

  /**
   * Prompts for and performs unlinking if accepted.
   *
   * @param document The document to unlink
   * @param access The access to remove
   * @param signal Optional abort signal
   * @returns A promise that resolves to:
   *  - 'unlinked' if the document was unlinked
   *  - 'deleted' if the document was deleted
   *  - false if the operation was cancelled or the user declined
   */
  async prompt(
    document: Document,
    access: DocumentAccess,
    signal?: AbortSignal,
  ) {
    // Close any ongoing prompt
    this.modal.close()

    // Start a new prompt
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.document = document
      this.access = access
      this.accessDetails = { type: 'Unknown' }
      this.modal.show()
    })

    // Abort the fetch if the dialog is closed
    const controller = new AbortController()
    void this._fetchAccessDetails(
      access,
      anyAbortSignal(signal, controller.signal),
    )

    // Wait for the user to confirm or cancel
    const response = await responsePromise
    this.modal.close()

    // Abort the fetch if the user cancels
    controller.abort()

    return response ?? false
  }

  /**
   * Fetches additional details about the access.
   */
  private async _fetchAccessDetails(
    access: DocumentAccess,
    signal?: AbortSignal,
  ) {
    if (access.accessType.type === 'Job') {
      const errorHandler = this._createSnackbarErrorHandler(
        `Loading document's related job details failed`,
      )

      try {
        const jobGist = await this._fetchJobGist(access.accessType.jobId)

        if (jobGist && !signal?.aborted) {
          runInAction(() => {
            this.accessDetails = { type: 'Job', job: jobGist }
          })
        }
      } catch (error) {
        // If fetching job gist fails, close the dialog
        this.modal.close()
        errorHandler(error)
      }
    }
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Removes the access.
   */
  unlink = task.resolved(async () => {
    if (!this.document || !this.access) {
      return
    }

    await this._documentStore
      .removeDocumentAccess(this.document.id, this.access.id)
      .catch(this._createSnackbarErrorHandler('Unlinking failed'))
    this._prompt.respond('unlinked')
    this.modal.close()
  })

  /**
   * Deletes the document permanently.
   */
  delete = task.resolved(async () => {
    if (!this.document) {
      return
    }

    const wasDeleted = await this._promptDelete(this.document)
    if (wasDeleted) {
      this._prompt.respond('deleted')
      this.modal.close()
    }
  })
}
